import React, { useEffect, useState } from "react";
import "./LandingPage.css";
import AkasLogo from "../../components/AkasLogo";

const featureGroups = [
  {
    code: "POS",
    title: "Kasir cepat, antrean singkat",
    description: "Transaksi tunai maupun digital tercatat rapi dalam beberapa klik—mudah dipelajari oleh tim baru.",
    tone: "lime",
  },
  {
    code: "STK",
    title: "Stok selalu dalam pantauan",
    description: "Pantau stok, bahan baku, pembelian, dan pergerakan barang dari satu tempat yang sama.",
    tone: "blue",
  },
  {
    code: "RPT",
    title: "Laporan yang mudah dipahami",
    description: "Lihat penjualan, margin, dan produk terlaris tanpa harus merapikan spreadsheet setiap malam.",
    tone: "orange",
  },
  {
    code: "CRM",
    title: "Pelanggan makin dikenal",
    description:
      "Simpan riwayat transaksi pelanggan untuk membangun layanan dan program loyalitas yang lebih personal.",
    tone: "pink",
  },
  {
    code: "TIM",
    title: "Tim bekerja sesuai peran",
    description: "Atur akses kasir, supervisor, dan owner agar operasional tetap aman dan mudah diawasi.",
    tone: "purple",
  },
  {
    code: "OUT",
    title: "Siap tumbuh ke banyak outlet",
    description: "Pantau performa setiap cabang dalam satu dashboard saat usaha mulai berkembang.",
    tone: "mint",
  },
];

const businessTypes = [
  { icon: "☕", title: "Kafe & Restoran", text: "Kelola menu, bahan baku, dan transaksi sibuk dengan lebih tenang." },
  { icon: "◫", title: "Toko Retail", text: "Stok akurat, pencarian produk cepat, dan laporan harian otomatis." },
  { icon: "✦", title: "Usaha Jasa", text: "Catat layanan, pelanggan, pembayaran, dan aktivitas tim dengan rapi." },
  { icon: "⌂", title: "Multi-outlet", text: "Satu pandangan untuk memantau banyak lokasi tanpa berpindah sistem." },
];

const pricingPlans = [
  {
    name: "AKAS UMKM",
    description: "Untuk warung, kedai, dan coffee shop kecil yang ingin operasional lebih rapi.",
    monthlyPrice: 89000,
    yearlyPrice: 75000,
    suffix: "/bulan",
    features: [
      "Semua fitur operasional inti AKAS",
      "1 outlet",
      "Maksimal 2 pengguna aktif",
      "Maksimal 200 produk",
      "Saldo layanan senilai paket",
      "Batas biaya transaksi Rp89.000/siklus",
      "Saldo tersisa tetap tersimpan",
    ],
  },
  {
    name: "AKAS Bisnis",
    description: "Untuk bisnis berkembang yang perlu mengontrol bahan baku, produksi, dan keuangan.",
    monthlyPrice: 119000,
    yearlyPrice: 99000,
    suffix: "/bulan",
    popular: true,
    features: [
      "Semua fitur operasional inti AKAS",
      "1 outlet",
      "Maksimal 10 pengguna aktif",
      "Maksimal 1.000 produk",
      "Saldo layanan senilai paket",
      "Batas biaya transaksi Rp119.000/siklus",
      "Saldo tersisa tetap tersimpan",
    ],
  },
  {
    name: "AKAS Premium",
    description: "Untuk brand multi-outlet dengan operasional dan pengawasan yang lebih kompleks.",
    monthlyPrice: 249000,
    yearlyPrice: 209000,
    suffix: "/bulan",
    features: [
      "Semua fitur operasional inti AKAS",
      "Hingga 5 outlet",
      "Maksimal 100 pengguna aktif",
      "Maksimal 10.000 produk",
      "Saldo layanan senilai paket",
      "Batas biaya transaksi Rp249.000/siklus",
      "Saldo tersisa tetap tersimpan",
    ],
  },
  {
    name: "AKAS Custom Solution",
    description: "Untuk jaringan usaha dan kebutuhan khusus yang memerlukan konfigurasi tersendiri.",
    custom: true,
    ctaLabel: "Konsultasikan kebutuhan",
    features: [
      "Lebih dari 5 outlet",
      "Kapasitas pengguna & data khusus",
      "Custom domain & branding",
      "Integrasi atau API sesuai kebutuhan",
      "Dedicated deployment",
      "Retensi backup & SLA khusus",
      "Migrasi data dan onboarding",
    ],
  },
];

const formatIDR = (value) => new Intl.NumberFormat("id-ID").format(value);

const faqs = [
  {
    question: "Apakah Akas cocok untuk usaha yang baru mulai?",
    answer:
      "Ya. Akas dirancang agar mudah dipelajari sejak hari pertama, sekaligus tetap siap mengikuti kebutuhan usaha saat transaksi, produk, dan outlet bertambah.",
  },
  {
    question: "Bisakah Akas digunakan untuk usaha kuliner dan retail?",
    answer:
      "Bisa. Alur transaksi, pengelolaan produk, stok, pelanggan, dan laporan Akas dapat dipakai untuk berbagai tipe UMKM, termasuk kuliner, retail, dan jasa.",
  },
  {
    question: "Apakah owner bisa memantau usaha tanpa berada di toko?",
    answer:
      "Bisa. Dashboard Akas membantu owner melihat ringkasan penjualan dan kondisi operasional dari perangkat yang terhubung ke internet.",
  },
  {
    question: "Apakah data dan akses karyawan dapat dibatasi?",
    answer: "Bisa. Setiap anggota tim dapat diberikan peran dan hak akses yang sesuai dengan tanggung jawabnya.",
  },
];

function Brand() {
  return (
    <span className="akas-brand" aria-label="Akas">
      <AkasLogo className="akas-brand-icon" decorative />
      <span>Akas</span>
    </span>
  );
}

function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <main className="akas-landing">
      <nav className={`akas-nav ${scrolled ? "is-scrolled" : ""}`} aria-label="Navigasi utama">
        <div className="akas-container akas-nav-inner">
          <a href="#beranda" className="akas-logo-link" onClick={closeMenu}>
            <Brand />
          </a>

          <button
            className={`akas-menu-button ${menuOpen ? "is-open" : ""}`}
            type="button"
            aria-label="Buka menu navigasi"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((value) => !value)}>
            <span />
            <span />
          </button>

          <div className={`akas-nav-menu ${menuOpen ? "is-open" : ""}`}>
            <a href="#fitur" onClick={closeMenu}>
              Fitur
            </a>
            <a href="#solusi" onClick={closeMenu}>
              Solusi Usaha
            </a>
            <a href="#harga" onClick={closeMenu}>
              Harga
            </a>
            <a href="#cara-kerja" onClick={closeMenu}>
              Cara Kerja
            </a>
            <a href="#faq" onClick={closeMenu}>
              FAQ
            </a>
            <div className="akas-nav-actions">
              <a className="akas-button akas-button-small" href="#mulai" onClick={closeMenu}>
                Mulai sekarang
              </a>
            </div>
          </div>
        </div>
      </nav>

      <section className="akas-hero" id="beranda">
        <div className="akas-hero-glow akas-hero-glow-one" />
        <div className="akas-hero-glow akas-hero-glow-two" />
        <div className="akas-container akas-hero-grid">
          <div className="akas-hero-copy">
            <div className="akas-eyebrow">
              <span /> Sistem operasional UMKM Indonesia
            </div>
            <h1>
              Usaha lebih rapi.
              <br />
              Tumbuh lebih <em>pasti.</em>
            </h1>
            <p>
              Akas menyatukan kasir, inventori, pelanggan, dan laporan usaha dalam satu aplikasi yang sederhana—supaya
              kamu punya lebih banyak waktu untuk mengembangkan bisnis.
            </p>
            <div className="akas-hero-actions">
              <a className="akas-button akas-button-primary" href="#mulai">
                Coba Akas sekarang <span>↗</span>
              </a>
              <a className="akas-text-button" href="#cara-kerja">
                <i aria-hidden="true">▶</i> Lihat cara kerja
              </a>
            </div>
            <div className="akas-hero-points" aria-label="Keunggulan utama">
              <span>
                <i>✓</i> Mudah digunakan
              </span>
              <span>
                <i>✓</i> Siap untuk banyak outlet
              </span>
              <span>
                <i>✓</i> Data terpusat
              </span>
            </div>
          </div>

          <div className="akas-product-stage" aria-label="Pratinjau dashboard Akas">
            <div className="akas-float-card akas-float-stock">
              <span className="akas-float-icon">↗</span>
              <div>
                <small>Stok aman</small>
                <strong>92%</strong>
              </div>
            </div>
            <div className="akas-float-card akas-float-sale">
              <span className="akas-float-avatar">R</span>
              <div>
                <small>Transaksi baru</small>
                <strong>Rp 186.000</strong>
              </div>
              <b>+1</b>
            </div>

            <div className="akas-dashboard-shell">
              <div className="akas-window-bar">
                <Brand />
                <div className="akas-window-search">⌕ &nbsp; Cari transaksi, produk...</div>
                <span className="akas-window-user">FM</span>
              </div>
              <div className="akas-dashboard-body">
                <aside className="akas-mini-sidebar" aria-hidden="true">
                  <span className="active">⌂</span>
                  <span>▦</span>
                  <span>◇</span>
                  <span>◎</span>
                  <span>◌</span>
                </aside>
                <div className="akas-dashboard-content">
                  <div className="akas-dashboard-heading">
                    <div>
                      <small>Senin, 14 Juli</small>
                      <strong>Selamat pagi, Faiz!</strong>
                    </div>
                    <button type="button">+ Transaksi baru</button>
                  </div>
                  <div className="akas-summary-grid">
                    <article>
                      <span>Penjualan hari ini</span>
                      <strong>Rp 8.420.000</strong>
                      <small>↗ 12,8% dari kemarin</small>
                    </article>
                    <article>
                      <span>Total transaksi</span>
                      <strong>128</strong>
                      <small>Rata-rata Rp 65.781</small>
                    </article>
                    <article>
                      <span>Stok menipis</span>
                      <strong>
                        6 <b>item</b>
                      </strong>
                      <small className="warning">Perlu diperiksa</small>
                    </article>
                  </div>
                  <div className="akas-chart-row">
                    <article className="akas-chart-card">
                      <div className="akas-card-title">
                        <div>
                          <strong>Ringkasan penjualan</strong>
                          <small>7 hari terakhir</small>
                        </div>
                        <span>Mingguan⌄</span>
                      </div>
                      <div className="akas-chart">
                        <div className="akas-chart-lines">
                          <i />
                          <i />
                          <i />
                          <i />
                        </div>
                        <div className="akas-bars">
                          {[44, 62, 48, 78, 66, 88, 96].map((height, index) => (
                            <i key={height} style={{ height: `${height}%` }} className={index === 6 ? "hot" : ""} />
                          ))}
                        </div>
                        <div className="akas-chart-labels">
                          <span>Sen</span>
                          <span>Sel</span>
                          <span>Rab</span>
                          <span>Kam</span>
                          <span>Jum</span>
                          <span>Sab</span>
                          <span>Min</span>
                        </div>
                      </div>
                    </article>
                    <article className="akas-product-list">
                      <div className="akas-card-title">
                        <div>
                          <strong>Produk terlaris</strong>
                          <small>Hari ini</small>
                        </div>
                        <span>•••</span>
                      </div>
                      <ul>
                        <li>
                          <i className="coffee">☕</i>
                          <span>
                            <b>Kopi Susu Akas</b>
                            <small>48 terjual</small>
                          </span>
                          <strong>Rp 864rb</strong>
                        </li>
                        <li>
                          <i className="food">◒</i>
                          <span>
                            <b>Rice Bowl Ayam</b>
                            <small>31 terjual</small>
                          </span>
                          <strong>Rp 775rb</strong>
                        </li>
                        <li>
                          <i className="tea">♨</i>
                          <span>
                            <b>Peach Tea</b>
                            <small>26 terjual</small>
                          </span>
                          <strong>Rp 390rb</strong>
                        </li>
                      </ul>
                    </article>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="akas-trust-strip" aria-label="Nilai utama Akas">
        <div className="akas-container akas-trust-grid">
          <p>
            Dirancang untuk ritme
            <br />
            <strong>UMKM Indonesia</strong>
          </p>
          <div>
            <span>01</span>
            <p>
              <strong>Satu sistem</strong>Kasir hingga laporan
            </p>
          </div>
          <div>
            <span>02</span>
            <p>
              <strong>Lebih jelas</strong>Data siap dibaca
            </p>
          </div>
          <div>
            <span>03</span>
            <p>
              <strong>Siap tumbuh</strong>Dari satu ke banyak outlet
            </p>
          </div>
        </div>
      </section>

      <section className="akas-section akas-intro-section" id="fitur">
        <div className="akas-container">
          <div className="akas-section-heading akas-heading-row">
            <div>
              <span className="akas-section-kicker">Semua dalam satu tempat</span>
              <h2>Bukan sekadar aplikasi kasir.</h2>
            </div>
            <p>
              Akas membantu operasional harian berjalan lebih ringkas, akurat, dan mudah dipantau—bahkan ketika kamu
              tidak sedang berada di toko.
            </p>
          </div>

          <div className="akas-feature-grid">
            {featureGroups.map((feature, index) => (
              <article className={`akas-feature-card tone-${feature.tone}`} key={feature.code}>
                <div className="akas-feature-top">
                  <span>{feature.code}</span>
                  <i>0{index + 1}</i>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <a href="#mulai" aria-label={`Pelajari ${feature.title}`}>
                  Pelajari fitur <span>↗</span>
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="akas-section akas-insight-section" id="cara-kerja">
        <div className="akas-container akas-insight-grid">
          <div className="akas-insight-visual">
            <div className="akas-phone-card">
              <div className="akas-phone-top">
                <Brand />
                <span>•••</span>
              </div>
              <small>Ringkasan hari ini</small>
              <strong>Rp 8.420.000</strong>
              <div className="akas-phone-chart">
                {[32, 56, 43, 70, 48, 82, 66, 94].map((height, index) => (
                  <i key={`${height}-${index}`} style={{ height: `${height}%` }} />
                ))}
              </div>
              <div className="akas-phone-stat">
                <span>
                  Transaksi<strong>128</strong>
                </span>
                <span>
                  Produk terjual<strong>294</strong>
                </span>
              </div>
            </div>
            <div className="akas-insight-note">
              <i>✓</i>
              <p>
                <strong>Data diperbarui otomatis</strong>
                <span>Pantau usaha dari mana saja.</span>
              </p>
            </div>
          </div>

          <div className="akas-insight-copy">
            <span className="akas-section-kicker light">Cara kerja Akas</span>
            <h2>Dari transaksi menjadi keputusan.</h2>
            <p>Setiap aktivitas usaha terhubung. Kamu tidak lagi menebak-nebak apa yang terjadi di toko.</p>
            <ol className="akas-steps">
              <li>
                <span>1</span>
                <div>
                  <strong>Catat tanpa ribet</strong>
                  <p>Transaksi penjualan dan pembelian masuk ke sistem secara teratur.</p>
                </div>
              </li>
              <li>
                <span>2</span>
                <div>
                  <strong>Data tersusun otomatis</strong>
                  <p>Stok, pelanggan, dan performa usaha ikut diperbarui.</p>
                </div>
              </li>
              <li>
                <span>3</span>
                <div>
                  <strong>Ambil keputusan lebih cepat</strong>
                  <p>Lihat ringkasan penting dan fokus pada peluang berikutnya.</p>
                </div>
              </li>
            </ol>
          </div>
        </div>
      </section>

      <section className="akas-section akas-business-section" id="solusi">
        <div className="akas-container">
          <div className="akas-section-heading centered">
            <span className="akas-section-kicker">Fleksibel untuk berbagai usaha</span>
            <h2>Satu Akas, banyak kemungkinan.</h2>
            <p>Fondasi operasional yang bisa menyesuaikan dengan cara usahamu bekerja.</p>
          </div>
          <div className="akas-business-grid">
            {businessTypes.map((business) => (
              <article key={business.title}>
                <span className="akas-business-icon">{business.icon}</span>
                <h3>{business.title}</h3>
                <p>{business.text}</p>
                <a href="#mulai">
                  Lihat solusi <span>→</span>
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="akas-section akas-pricing-section" id="harga">
        <div className="akas-container">
          <div className="akas-pricing-heading">
            <div>
              <span className="akas-section-kicker">Harga transparan, tanpa kejutan</span>
              <h2>Pilih paket yang tumbuh bersama usahamu.</h2>
              <p>Mulai dari kebutuhan paling esensial, lalu tingkatkan kapan pun operasionalmu berkembang.</p>
            </div>
            <div className="akas-billing-toggle" role="group" aria-label="Pilih periode pembayaran">
              <button
                type="button"
                className={billingCycle === "monthly" ? "active" : ""}
                onClick={() => setBillingCycle("monthly")}>
                Bulanan
              </button>
              <button
                type="button"
                className={billingCycle === "yearly" ? "active" : ""}
                onClick={() => setBillingCycle("yearly")}>
                Tahunan <span>Hemat hingga 20%</span>
              </button>
            </div>
          </div>
          <div className="akas-pricing-grid">
            {pricingPlans.map((plan) => {
              const price = billingCycle === "yearly" ? plan.yearlyPrice : plan.monthlyPrice;
              return (
                <article className={`akas-price-card ${plan.popular ? "is-popular" : ""}`} key={plan.name}>
                  {plan.popular ? <div className="akas-popular-label">Paling populer</div> : null}
                  <div className="akas-price-head">
                    <span className="akas-plan-name">{plan.name}</span>
                    <p>{plan.description}</p>
                    <div className={`akas-price-value ${plan.custom ? "is-custom" : ""}`}>
                      {plan.custom ? (
                        <strong>Hubungi Kami</strong>
                      ) : (
                        <>
                          <small>Rp</small>
                          <strong>{formatIDR(price)}</strong>
                          <span>{plan.suffix}</span>
                        </>
                      )}
                    </div>
                    <div className="akas-billing-note">
                      {plan.custom
                        ? "Harga disusun sesuai ruang lingkup kebutuhan"
                        : billingCycle === "yearly"
                          ? `Rp${formatIDR(price * 12)} ditagihkan per tahun`
                          : "Ditagihkan setiap bulan"}
                    </div>
                  </div>
                  <a className={`akas-price-button ${plan.popular ? "primary" : ""}`} href="#mulai">
                    {plan.ctaLabel || `Pilih ${plan.name}`} <span>↗</span>
                  </a>
                  <div className="akas-feature-list">
                    <strong>Yang kamu dapatkan:</strong>
                    <ul>
                      {plan.features.map((feature) => (
                        <li key={feature}>
                          <i>✓</i>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              );
            })}
          </div>
          <div className="akas-pricing-footnote">
            <span>✓</span> Setiap pembayaran memberi masa aktif dan saldo layanan senilai paket. Transaksi lunas memakai
            saldo 0,7% hingga batas biaya paket per siklus; sisa saldo tidak hangus. Harga belum termasuk pajak. Butuh lebih?{" "}
            <a href="#mulai">Bicarakan AKAS Custom Solution →</a>
          </div>
        </div>
      </section>

      <section className="akas-section akas-owner-section">
        <div className="akas-container akas-owner-card">
          <div className="akas-owner-copy">
            <span className="akas-section-kicker light">Dibuat untuk owner yang ingin maju</span>
            <h2>
              Lebih sedikit menebak.
              <br />
              Lebih banyak bertumbuh.
            </h2>
            <p>
              Bangun kebiasaan kerja yang rapi sejak awal. Akas menjaga data usahamu tetap tersusun saat bisnis bergerak
              semakin cepat.
            </p>
            <a className="akas-button akas-button-light" href="#mulai">
              Kenali Akas <span>↗</span>
            </a>
          </div>
          <div className="akas-owner-quote">
            <span className="akas-quote-mark">“</span>
            <blockquote>
              Yang kamu butuhkan bukan lebih banyak aplikasi, tetapi satu sistem yang membuat semuanya terasa lebih
              sederhana.
            </blockquote>
            <div>
              <i>
                <span>A</span>
              </i>
              <p>
                <strong>Prinsip Akas</strong>
                <small>Teknologi yang bekerja untuk usaha</small>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="akas-section akas-faq-section" id="faq">
        <div className="akas-container akas-faq-grid">
          <div className="akas-faq-heading">
            <span className="akas-section-kicker">Pertanyaan umum</span>
            <h2>Hal yang mungkin ingin kamu tanyakan.</h2>
            <p>Belum menemukan jawaban yang dicari? Tim Akas siap membantu mengenali kebutuhan usahamu.</p>
            <a href="#mulai">
              Bicara dengan tim kami <span>↗</span>
            </a>
          </div>
          <div className="akas-accordion">
            {faqs.map((faq, index) => {
              const isActive = activeFaq === index;
              return (
                <article className={isActive ? "is-active" : ""} key={faq.question}>
                  <button type="button" aria-expanded={isActive} onClick={() => setActiveFaq(isActive ? -1 : index)}>
                    <span>{faq.question}</span>
                    <i>{isActive ? "−" : "+"}</i>
                  </button>
                  <div className="akas-answer">
                    <p>{faq.answer}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="akas-final-cta" id="mulai">
        <div className="akas-container akas-cta-card">
          <div className="akas-cta-orb one" />
          <div className="akas-cta-orb two" />
          <span className="akas-section-kicker light">Mulai langkah yang lebih rapi</span>
          <h2 className="text-light">
            Usahamu sudah bergerak.
            <br />
            Sekarang waktunya <em>melaju.</em>
          </h2>
          <p>Satukan operasional usaha bersama Akas dan lihat bisnis dengan lebih jelas setiap hari.</p>
          <div className="akas-cta-actions">
            <a className="akas-button akas-button-light" href="#harga">
              Pilih paket Akas <span>↗</span>
            </a>
            <a className="akas-cta-link" href="#fitur">
              Pelajari fiturnya
            </a>
          </div>
        </div>
      </section>

      <footer className="akas-footer">
        <div className="akas-container">
          <div className="akas-footer-main">
            <div className="akas-footer-brand">
              <Brand />
              <p>Sistem operasional yang membuat UMKM Indonesia bekerja lebih rapi dan tumbuh lebih pasti.</p>
            </div>
            <div className="akas-footer-links">
              <strong>Produk</strong>
              <a href="#fitur">Fitur</a>
              <a href="#cara-kerja">Cara kerja</a>
              <a href="#solusi">Solusi usaha</a>
              <a href="#harga">Harga</a>
            </div>
            <div className="akas-footer-links">
              <strong>Perusahaan</strong>
              <a href="#beranda">Tentang Akas</a>
              <a href="#faq">Pusat bantuan</a>
              <a href="#mulai">Hubungi kami</a>
            </div>
            <div className="akas-footer-links">
              <strong>Dukungan</strong>
              <a href="#faq">Pertanyaan umum</a>
              <a href="#cara-kerja">Panduan onboarding</a>
            </div>
          </div>
          <div className="akas-footer-bottom">
            <span>© {new Date().getFullYear()} Akas. Dibuat untuk UMKM Indonesia.</span>
            <div>
              <a href="#beranda">Privasi</a>
              <a href="#beranda">Ketentuan</a>
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
}

export default LandingPage;
